import { InputHTMLAttributes, useId } from 'react';

import { V2Types } from '@xemplo/popover';
import { SingleSelectInputProps } from '@xemplo/stateful-list';
import { XemploInputProps } from '@xemplo/xemplo-input';

import { UseDropdownProps } from './hooks.types';
import { useDropdownEvents } from './use-dropdown-events';

export function useDropdownMetadata(props: UseDropdownProps) {
  const {
    allowDeselect,
    value, // to ensure this is not spread into rest
    displayValue, // to ensure this is not spread into rest
    setDisplayValue, // to ensure this is not spread into rest
    popoverRef, // to ensure this is not spread into rest
    onSearchChange, // to ensure this is not spread into rest
    resolveLabel, // to ensure this is not spread into rest
    items,
    name,
    offset,
    placement,
    searchable,
    useAutoPlacement,
    onListChange,
    ...rest
  } = props;

  const inputWrapperUniqueId = useId();
  const inputId = `${inputWrapperUniqueId}${props.id ?? props.name}`;
  const dropdownEvents = useDropdownEvents(props);

  const rootProps = {
    offset,
    placement,
    useAutoPlacement,
    onOpenChange: dropdownEvents.handleOnOpenChange,
    trigger: V2Types.PopoverTrigger.Click,
  } satisfies V2Types.PopoverProps;

  const hiddenInputProps = {
    name,
    value: dropdownEvents.value,
    type: 'hidden',
  } satisfies InputHTMLAttributes<HTMLInputElement>;

  const displayInputProps = {
    ...rest,
    value: dropdownEvents.displayValue,
    wrapperId: inputId,
    type: 'text',
    name: `display-${props.name}`, // To avoid conflict with the hidden input
    onKeyDown: dropdownEvents.handleKeyDown,
    onFocus: dropdownEvents.handleFocus,
    containerOnClick: dropdownEvents.handleContainerClick,
    onClick: dropdownEvents.handleInputClick,
  } satisfies XemploInputProps;

  const listProps = {
    items,
    initialSearch: dropdownEvents.displayValue,
    disableSearch: false,
    defaultSelected: dropdownEvents.value,
    allowDeselect,
    triggerId: inputId,
    onChange: dropdownEvents.handleListChange,
  } satisfies SingleSelectInputProps;

  return {
    searchable,
    rootProps,
    listProps,
    displayInputProps,
    hiddenInputProps,
  };
}
